import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  HiOutlineExclamationCircle,
  HiOutlineDocumentSearch,
  HiRefresh,
} from "react-icons/hi";
import { RiCloseFill } from "react-icons/ri";
import MeritoButton from "../common/MeritoButtons/MeritoButtons";
import MeritoPasswordField from "../common/MeritoPasswordField/MeritoPasswordField";
import CircularLoader from "../common/CircularLoader/CircularLoader";
import pdfFileIcon from "../../assets/images/bankStatements/pdf-file.svg";
import identificationErrorIcon from "../../assets/images/bankStatements/identification-error.svg";
import passwordVerifiedIcon from "../../assets/images/bankStatements/password-verified.svg";
import { fetchBankStatementDocument } from "./api";
import { PulseDocSummary } from "./types";
import {
  PASSWORD_VERIFIED_DURATION_MS,
  formatDateRange,
  getDocumentName,
  isDocumentDeletable,
  isDocumentIdentificationFailed,
  isDocumentPasswordRequired,
  sortByPriority,
} from "./utils";
import { getApiErrorMessage } from "../../utils/getApiErrorMessage";
import styles from "./BankStatements.module.scss";

const IDENTIFICATION_ERROR_FALLBACK =
  "Couldn't recognise this statement's format. Try a different file.";

export const ViewDocumentButton = ({
  file,
  fileKey,
  onError,
}: {
  file?: File;
  fileKey?: string;
  onError?: (message: string) => void;
}) => {
  const [loading, setLoading] = useState(false);

  const handleView = async () => {
    const previewWindow = window.open("", "_blank");
    if (!previewWindow) {
      onError?.("Allow pop-ups to view this statement.");
      return;
    }

    try {
      previewWindow.opener = null;
      setLoading(true);
      onError?.("");
      const sourceBlob =
        file || (fileKey ? await fetchBankStatementDocument(fileKey) : null);
      if (!sourceBlob) {
        throw new Error("This statement is not available to view.");
      }
      const pdfBlob = new Blob([sourceBlob], { type: "application/pdf" });
      const previewUrl = URL.createObjectURL(pdfBlob);
      previewWindow.location.replace(previewUrl);
      window.setTimeout(() => URL.revokeObjectURL(previewUrl), 60_000);
    } catch (error) {
      previewWindow.close();
      onError?.(getApiErrorMessage(error, "This statement could not be opened."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <span title="View statement">
      <MeritoButton
        type="tertiary"
        size="md2"
        onlyIcon
        hover={false}
        disabled={loading}
        icon={<HiOutlineDocumentSearch />}
        onClick={handleView}
      />
    </span>
  );
};

export const PdfFileIcon = () => (
  <img
    className={styles.pdfIcon}
    src={pdfFileIcon}
    alt="PDF"
  />
);

export const PasswordVerificationSuccess = () => (
  <div className={styles.passwordVerifiedPanel} role="status">
    <img src={passwordVerifiedIcon} alt="" />
    <span>Password verified.</span>
  </div>
);

export const DocumentRow = ({
  doc,
  onRemove,
  onSubmitPassword,
  removing = false,
  processing = false,
  icon,
  showView = true,
}: {
  doc: PulseDocSummary;
  onRemove?: () => void | Promise<void>;
  onSubmitPassword?: (
    password: string,
    signal?: AbortSignal
  ) => Promise<void>;
  removing?: boolean;
  /** Force the spinner — for work the doc's own OCR status doesn't reflect (e.g. Pulse financials classification). */
  processing?: boolean;
  /** Replaces the PDF icon, e.g. for an Excel upload. */
  icon?: React.ReactNode;
  /** Hide the "view statement" button where a preview makes no sense. */
  showView?: boolean;
}) => {
  const [viewError, setViewError] = useState("");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [localPasswordVerifiedAt, setLocalPasswordVerifiedAt] = useState<
    number | null
  >(null);
  const passwordVerificationControllerRef = useRef<AbortController | null>(
    null
  );
  const deletable = isDocumentDeletable(doc) && !processing;
  const passwordRequired = isDocumentPasswordRequired(doc);
  const identificationFailed = isDocumentIdentificationFailed(doc);
  const dateRange = formatDateRange(doc.startDate, doc.endDate);
  const documentName = getDocumentName(doc);
  const documentError =
    viewError ||
    (identificationFailed
      ? doc.errorMessage || IDENTIFICATION_ERROR_FALLBACK
      : doc.errorMessage);
  const showIdentificationError = identificationFailed && !viewError;
  const [showPasswordVerified, setShowPasswordVerified] = useState(() => {
    const verifiedAt = doc.passwordVerifiedAt ?? localPasswordVerifiedAt;
    return (
      verifiedAt != null &&
      Date.now() - verifiedAt < PASSWORD_VERIFIED_DURATION_MS
    );
  });

  useEffect(() => {
    const verifiedAt = doc.passwordVerifiedAt ?? localPasswordVerifiedAt;
    if (verifiedAt == null) {
      setShowPasswordVerified(false);
      return;
    }

    const remaining = PASSWORD_VERIFIED_DURATION_MS - (Date.now() - verifiedAt);
    if (remaining <= 0) {
      setShowPasswordVerified(false);
      return;
    }

    setShowPasswordVerified(true);
    const timer = window.setTimeout(
      () => setShowPasswordVerified(false),
      remaining
    );
    return () => window.clearTimeout(timer);
  }, [doc.id, doc.passwordVerifiedAt, localPasswordVerifiedAt]);

  useEffect(
    () => () => passwordVerificationControllerRef.current?.abort(),
    []
  );

  const handlePasswordSubmit = async () => {
    if (!password || !onSubmitPassword || passwordLoading) return;
    const controller = new AbortController();
    passwordVerificationControllerRef.current?.abort();
    passwordVerificationControllerRef.current = controller;
    setPasswordLoading(true);
    setPasswordError("");
    try {
      await onSubmitPassword(password, controller.signal);
      if (controller.signal.aborted) return;
      setLocalPasswordVerifiedAt(Date.now());
      setShowPasswordVerified(true);
      setPassword("");
    } catch (error) {
      if (controller.signal.aborted) return;
      setPasswordError(
        getApiErrorMessage(error, "This PDF could not be verified.")
      );
    } finally {
      if (passwordVerificationControllerRef.current === controller) {
        passwordVerificationControllerRef.current = null;
      }
      setPasswordLoading(false);
    }
  };

  const handleRemove = () => {
    passwordVerificationControllerRef.current?.abort();
    passwordVerificationControllerRef.current = null;
    return onRemove?.();
  };

  const row = (
    <div className={styles.documentRow}>
      {icon ?? <PdfFileIcon />}
      <div className={styles.documentDetails}>
        <div className={styles.documentName} title={documentName}>
          {documentName}
        </div>
        {dateRange ? (
          <div className={styles.documentDateRange}>{dateRange}</div>
        ) : null}
        {!showPasswordVerified && documentError ? (
          <div
            className={`${styles.documentError} ${
              showIdentificationError
                ? styles.identificationDocumentError
                : ""
            }`}
            title={documentError}
          >
            {showIdentificationError ? (
              <img src={identificationErrorIcon} alt="" />
            ) : null}
            <span>{documentError}</span>
          </div>
        ) : null}
      </div>
      <div
        className={`${styles.documentActions} ${
          showView ? "" : styles.singleAction
        }`}
      >
        {showView ? (
          <ViewDocumentButton
            fileKey={doc.s3FileKey}
            onError={setViewError}
          />
        ) : null}
        {showPasswordVerified ? (
          <span className={styles.documentProcessing} title="Processing">
            <CircularLoader width="1.5rem" height="1.5rem" />
          </span>
        ) : deletable && onRemove ? (
          <span title="Delete statement">
            <MeritoButton
              type="tertiary"
              size="md2"
              onlyIcon
              hover={false}
              disabled={removing}
              loading={removing}
              icon={<RiCloseFill />}
              onClick={handleRemove}
            />
          </span>
        ) : !deletable ? (
          <span className={styles.documentProcessing} title="Processing">
            <CircularLoader width="1.5rem" height="1.5rem" />
          </span>
        ) : (
          <span
            className={styles.documentActionPlaceholder}
            aria-hidden="true"
          />
        )}
      </div>
    </div>
  );

  if (showPasswordVerified) {
    return (
      <div className={styles.passwordVerifiedFileItem}>
        {row}
        <PasswordVerificationSuccess />
      </div>
    );
  }

  if (!passwordRequired) {
    return row;
  }

  return (
    <div className={styles.passwordDocumentItem}>
      {row}
      <PasswordInput
        value={password}
        error={passwordError}
        loading={passwordLoading}
        onChange={(value) => {
          setPassword(value);
          setPasswordError("");
        }}
        onSubmit={handlePasswordSubmit}
      />
    </div>
  );
};

export const DocumentList = ({
  documents,
  onRemove,
  onSubmitPassword,
  embedded = false,
}: {
  documents: PulseDocSummary[];
  onRemove?: (doc: PulseDocSummary) => Promise<void> | void;
  onSubmitPassword?: (
    doc: PulseDocSummary,
    password: string,
    signal?: AbortSignal
  ) => Promise<void>;
  embedded?: boolean;
}) => {
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [removeError, setRemoveError] = useState("");
  const orderedDocuments = useMemo(
    () =>
      sortByPriority(
        documents,
        (doc) => {
          if (isDocumentPasswordRequired(doc)) return 0;
          if (!isDocumentDeletable(doc)) return 1;
          return 2;
        },
        (doc) => {
          const createdAtTime = doc.createdAt
            ? Date.parse(doc.createdAt)
            : Number.NaN;
          return Number.isFinite(createdAtTime) ? createdAtTime : doc.id;
        }
      ),
    [documents]
  );

  const handleRemove = async (doc: PulseDocSummary) => {
    if (!onRemove) return;
    setRemovingId(doc.id);
    setRemoveError("");
    try {
      await onRemove(doc);
    } catch (error) {
      setRemoveError(
        getApiErrorMessage(error, "This statement could not be removed.")
      );
    } finally {
      setRemovingId(null);
    }
  };

  const rows = orderedDocuments.map((doc) => (
    <DocumentRow
      key={doc.id}
      doc={doc}
      removing={removingId === doc.id}
      onRemove={onRemove ? () => handleRemove(doc) : undefined}
      onSubmitPassword={
        onSubmitPassword
          ? (password, signal) => onSubmitPassword(doc, password, signal)
          : undefined
      }
    />
  ));

  if (embedded) {
    return (
      <>
        {rows}
        {removeError ? (
          <div className={styles.inlineError}>{removeError}</div>
        ) : null}
      </>
    );
  }

  return (
    <div>
      <div className={styles.documentList}>{rows}</div>
      {removeError ? (
        <div className={styles.inlineError}>{removeError}</div>
      ) : null}
    </div>
  );
};

export const PasswordInput = ({
  value,
  error,
  loading,
  message = "This file is password-protected. Enter the password to continue.",
  onChange,
  onSubmit,
}: {
  value: string;
  error?: string;
  loading?: boolean;
  message?: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
}) => {
  return (
    <div className={styles.passwordPanel}>
      <div className={styles.passwordMessage}>
        <HiOutlineExclamationCircle />
        <span>{message}</span>
      </div>
      <div className={styles.passwordActions}>
        <MeritoPasswordField
          value={value}
          error={!!error}
          disabled={loading}
          onChange={onChange}
          onEnter={onSubmit}
        />
        <MeritoButton
          type="inverse"
          size="md"
          btnText="Submit"
          loading={loading}
          showLoadingWithText
          disabled={loading}
          hover={false}
          className={styles.passwordSubmit}
          onClick={onSubmit}
        />
      </div>
      {error ? (
        <div className={styles.passwordError} role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
};

export const RetryAction = ({
  loading,
  onClick,
}: {
  loading?: boolean;
  onClick: () => void;
}) => (
  <MeritoButton
    type="secondary-1"
    size="md2"
    icon={<HiRefresh />}
    btnText="Retry"
    loading={loading}
    onClick={onClick}
  />
);
